import * as React from 'react';
import DataObjectParser from 'dataobject-parser';
import get from 'lodash/get';
import has from 'lodash/has';
import isString from 'lodash/isString';
import { useDispatch } from 'react-redux';
import { contractsResource, fetchContracts, computeLabel as computeContractLabel, selectContracts } from '@gandi/contracts';
import { notifyDanger } from '@gandi/notifications';
import { AddressField, PhoneField } from '@gandi/react-places';
import { useTranslator } from '@gandi/translate';
import { formValueSelector, CheckboxField, Form, InputTextField, PasswordField, SwitchField, SelectField, getFormSubmitErrors, unprotectAuthorizedHtml } from 'gandi.react-form';
import { useAppSelector } from '../hooks/useAppSelector';
import { contactTypeReseller, maybeConvertType } from './libs/definitions';
import { getBaseContactFields, getIsFieldDisabledFn } from './libs/fields';
import { fetchDomainExtraParams } from './modules/actions';
import { selectRawExtraFields } from './modules/selectors';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var styles = {
  "switchWithSublabel": "ContactForm-switchWithSublabel_1NtrL"
};
export const CONTACTS_FORM = 'contacts-form';
const contractId = 'g1';
function renderLabel(label) {
  if (!label) {
    return '';
  }
  return label;
}
function ContactForm(props) {
  const {
    initialValues,
    fqdn,
    tld,
    role,
    phase,
    process,
    formProps,
    isWorking,
    newOwner,
    handleSubmit,
    ownerSharingId
  } = props;
  const dispatch = useDispatch();
  const translator = useTranslator();
  const {
    __
  } = translator;
  const formId = formProps.form || CONTACTS_FORM;
  const selectFormValues = React.useMemo(() => formValueSelector(formId), [formId]);
  const contractsParams = React.useMemo(() => ({
    contractId,
    sharingId: ownerSharingId
  }), [ownerSharingId]);
  const contractsLabel = React.useMemo(() => computeContractLabel(contractsParams), [contractsParams]);
  const shouldFetchContracts = useAppSelector(state => contractsResource.selectShouldFetch(state, contractsLabel));
  const extraFields = useAppSelector(selectRawExtraFields);
  const isIcannContractAccepted = useAppSelector(state => selectFormValues(state, 'icann_contract_accept') || false);
  const contracts = useAppSelector(state => selectContracts(state, contractsLabel));
  const formSubmittedErrors = useAppSelector(getFormSubmitErrors(formId));
  const formErrors = React.useMemo(() => unprotectAuthorizedHtml(DataObjectParser.untranspose(formSubmittedErrors)), [formSubmittedErrors]);
  const [currentContactType, setCurrentContactType] = React.useState(maybeConvertType(initialValues.type));
  React.useEffect(() => {
    dispatch(fetchDomainExtraParams({
      fqdn,
      tld,
      phase
    }, role, currentContactType)).catch(err => err.errors?._error ? dispatch(notifyDanger(err.errors._error)) : console.error({
      err
    }));
  }, [currentContactType, dispatch, fqdn, phase, role, tld]);
  React.useEffect(() => {
    if (role === 'owner' && process === 'edition' && shouldFetchContracts && contractsParams.sharingId) {
      dispatch(fetchContracts(contractsParams));
    }
  }, []);
  React.useEffect(() => {
    formProps.initialize(initialValues);
  }, []);
  const handleSelectChange = React.useCallback(field => evt => {
    const target = evt.currentTarget;
    formProps.change(target.name, target.value);
    if (field.field === 'type') {
      const type = target.value;
      setCurrentContactType(type);
    }
  }, [formProps]);
  const handleCheckboxChange = React.useCallback(evt => {
    if (evt.currentTarget.value) {
      formProps.change('email', initialValues.email);
    }
  }, [formProps, initialValues.email]);
  const renderHelp = React.useCallback((field, disabled) => {
    if (formErrors && formErrors[field.field] === field.description) {
      return null;
    }
    const helpMessage = [];
    if (field.description) {
      if (isString(field.description)) {
        helpMessage.push( /*#__PURE__*/_jsx("span", {
          dangerouslySetInnerHTML: {
            __html: field.description
          }
        }, `help-message-field-description-${field.field}`));
        helpMessage.push( /*#__PURE__*/_jsx("br", {}, `help-message-blank-${field.field}`));
      } else {
        helpMessage.push( /*#__PURE__*/_jsx("span", {
          children: field.description
        }, `help-message-field-description-${field.field}`));
        helpMessage.push( /*#__PURE__*/_jsx("br", {}, `help-message-blank-${field.field}`));
      }
    }
    if (disabled && field.field !== 'email') {
      if (process === 'create' || process === 'transfer') {
        return /*#__PURE__*/_jsx("span", {
          dangerouslySetInnerHTML: __('This information cannot be modified. ' + 'If you need to update this information, please go back ' + 'and change the invoicing organization. We will use that ' + "data for the domain's owner.", {
            withHTML: true
          })
        }, `help-message-disabled-field-${field.field}`);
      }
      if (process === 'edition') {
        return /*#__PURE__*/_jsx("span", {
          dangerouslySetInnerHTML: __('This information cannot be modified. In order to update ' + 'the owner information, please proceed with an owner change of ' + 'the domain.<br>To learn how to perform an owner change please ' + 'see <a href="https://docs.gandi.net/en/domain_names/change_owner/standard_process.html" target="_blank" rel="noopener noreferrer">our documentation.</a>', {
            withHTML: true
          })
        }, `help-message-disabled-field-${field.field}`);
      }
      return null;
    }
    return helpMessage;
  }, [__, formErrors, process]);
  const renderInputTextField = React.useCallback((field, disabler) => {
    const disabled = disabler(field.field || '');
    return /*#__PURE__*/_jsx(InputTextField, {
      name: field.field,
      label: renderLabel(field.label),
      required: field.required,
      disabled: disabled,
      help: renderHelp(field, disabled)
    }, field.field);
  }, [renderHelp]);
  const renderInputAddressField = React.useCallback(field => {
    const displayedFields = ['streetAddr', 'zip', 'city', 'subdivisions', 'country'];
    return /*#__PURE__*/_jsx(AddressField, {
      required: field.required,
      disabled: isWorking || false,
      forceMandatorySubdivision: true,
      displayedFields: displayedFields,
      ...formProps
    });
  }, [formProps, isWorking]);
  const renderPhoneField = React.useCallback(field => {
    return /*#__PURE__*/_jsx(PhoneField, {
      name: field.field,
      label: renderLabel(field.label),
      required: field.required,
      disabled: isWorking || false
    });
  }, [isWorking]);
  const renderInputPasswordField = React.useCallback(field => {
    return /*#__PURE__*/_jsx(PasswordField, {
      name: field.field,
      label: renderLabel(field.label),
      required: field.required,
      disabled: isWorking || false,
      help: renderHelp(field)
    }, field.field);
  }, [isWorking, renderHelp]);
  const renderSelectField = React.useCallback((field, disabler) => {
    const disabled = disabler(field.field || '');
    const copiedValues = [].concat(field.propsValuesName ? get(props, field.propsValuesName, []) : get(field, 'values', []));
    if (field.field === 'type' && disabled) {
      copiedValues.push(contactTypeReseller);
    }
    const optionsValues = copiedValues.map(value => ({
      value: value.value,
      text: __(value.label)
    }));
    const extraProps = {};
    if (!optionsValues.length) {
      extraProps.disabled = true;
    }
    return /*#__PURE__*/_jsx(SelectField, {
      variant: ['block'],
      name: field.field,
      label: renderLabel(field.label),
      required: field.required,
      disabled: disabled,
      help: renderHelp(field, disabled),
      values: optionsValues,
      onChange: handleSelectChange(field),
      placeholder: __('Select'),
      ...extraProps
    }, field.field);
  }, [__, handleSelectChange, props, renderHelp]);
  const renderSwitchField = React.useCallback(field => {
    const defaultChecked = has(initialValues, field.field) ? initialValues[field.field] : field.defaultValue;
    return /*#__PURE__*/_jsx(SwitchField, {
      name: field.field,
      label: renderLabel(field.label),
      required: field.required,
      disabled: isWorking || false,
      help: renderHelp(field),
      defaultChecked: Boolean(defaultChecked),
      className: styles.switchWithSublabel,
      children: field.sublabel
    }, field.field);
  }, [initialValues, isWorking, renderHelp]);
  const renderCheckboxField = React.useCallback(field => {
    let disabled = isWorking;
    if (field.field === 'icann_contract_accept') {
      disabled = disabled || contracts.find(contract => contract.identifier === 'g1') === undefined;
    }
    return /*#__PURE__*/_jsx(CheckboxField, {
      name: field.field,
      required: field.required,
      disabled: disabled,
      onChange: handleCheckboxChange,
      help: renderHelp(field),
      children: renderLabel(field.label)
    }, field.field);
  }, [contracts, handleCheckboxChange, isWorking, renderHelp]);
  const disabler = React.useMemo(() => getIsFieldDisabledFn({
    process,
    contactType: currentContactType,
    role,
    newOwner,
    isIcannContractAccepted,
    vatNumber: initialValues && initialValues.vat_number || initialValues.extra_parameters && initialValues.extra_parameters.vat_number || ''
  }), [currentContactType, initialValues, isIcannContractAccepted, newOwner, process, role]);
  const renderField = React.useCallback(field => {
    const extendedField = {
      ...field
    };
    switch (extendedField.type) {
      case 'string':
        return renderInputTextField(extendedField, disabler);
      case 'address':
        return renderInputAddressField(extendedField);
      case 'phone':
        return renderPhoneField(extendedField);
      case 'password':
        return renderInputPasswordField(extendedField);
      case 'enum':
        return renderSelectField(extendedField, disabler);
      case 'boolean':
        return renderSwitchField(extendedField);
      case 'checkbox':
        return renderCheckboxField(extendedField);
      default:
        return renderInputTextField(extendedField, disabler);
    }
  }, [disabler, renderCheckboxField, renderInputAddressField, renderInputPasswordField, renderInputTextField, renderPhoneField, renderSelectField, renderSwitchField]);
  const fields = React.useMemo(() => getBaseContactFields(translator, currentContactType, role, process, contracts), [contracts, currentContactType, process, role, translator]);
  return /*#__PURE__*/_jsxs(Form, {
    id: formProps.form || CONTACTS_FORM,
    onSubmit: handleSubmit,
    className: "form-stacked",
    children: [fields.map(field => /*#__PURE__*/_jsx("div", {
      children: renderField(field)
    }, field.field)), Array.isArray(extraFields) && Boolean(extraFields.length) && /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("h3", {
        children: __('Extra parameters')
      }), extraFields.map(field => ({
        ...field,
        field: `extra_parameters.${field.field || ''}`
      })).map(renderField)]
    })]
  });
}
export default ContactForm;